<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="920"
    scrollable
  >
    <v-card class="image-picker">
      <header class="image-picker-header">
        <div>
          <h2>{{ title }}</h2>
          <p>{{ subtitle }}</p>
        </div>

        <v-btn
          icon="mdi-close"
          variant="text"
          aria-label="Close image picker"
          @click="closeModal"
        />
      </header>

      <div class="image-picker-tabs">
        <v-btn-toggle
          v-model="activeTab"
          mandatory
          divided
          density="compact"
          variant="outlined"
        >
          <v-btn value="library">
            <v-icon start>mdi-image-multiple-outline</v-icon>
            Library
          </v-btn>
          <v-btn value="upload">
            <v-icon start>mdi-cloud-upload-outline</v-icon>
            Upload
          </v-btn>
          <v-btn v-if="showStock" value="stock">
            <v-icon start>mdi-image-frame</v-icon>
            Stock
          </v-btn>
        </v-btn-toggle>
      </div>

      <template v-if="activeTab === 'library' || activeTab === 'stock'">
        <div v-if="activeTab === 'library'" class="image-picker-search">
          <!-- Search stays local to the modal so BuilderView only manages selected images. -->
          <v-text-field
            v-model="search"
            label="Search images"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
          />
        </div>

        <v-card-text class="image-picker-body">
          <div
            v-if="activeTab === 'library' ? loading : stockLoading"
            class="image-picker-state"
          >
            <v-progress-circular indeterminate />
            <span>Loading images...</span>
          </div>

          <div
            v-else-if="!currentTabImages.length"
            class="image-picker-state"
          >
            <v-icon size="32">mdi-image-off-outline</v-icon>
            <span>{{ activeTab === 'library' ? emptyText : stockEmptyText }}</span>
          </div>

          <div
            v-else
            class="image-picker-grid"
          >
            <div
              v-for="image in currentTabImages"
              :key="image.id"
              class="image-picker-tile"
              :class="{
                selected: image.url === selectedUrl,
                failed: failedImageIds.has(image.id),
              }"
            >
              <button
                type="button"
                class="image-picker-tile-select"
                :disabled="failedImageIds.has(image.id)"
                @click="selectImage(image)"
              >
                <div class="image-picker-thumbnail">
                  <!-- The thumbnail can differ from the final URL if your API returns both. -->
                  <img
                    v-if="!failedImageIds.has(image.id)"
                    :src="image.thumbnailUrl"
                    :alt="image.label"
                    loading="lazy"
                    @error="handleThumbnailError(image, $event)"
                  />
                  <div
                    v-else
                    class="image-picker-thumbnail-error"
                    role="img"
                    :aria-label="`${image.label} is unavailable`"
                  >
                    <v-icon size="28">mdi-image-broken-variant</v-icon>
                    <span>Image unavailable</span>
                  </div>
                </div>
                <span class="image-picker-tile-label">{{ image.label }}</span>
              </button>

              <button
                v-if="deletable && activeTab === 'library'"
                type="button"
                class="image-picker-tile-delete"
                :disabled="deleting === image.id"
                :title="deleting === image.id ? 'Deleting…' : 'Delete image'"
                @click.stop="emit('delete', image.raw)"
              >
                <v-icon size="14">{{ deleting === image.id ? 'mdi-loading mdi-spin' : 'mdi-trash-can-outline' }}</v-icon>
              </button>
            </div>
          </div>
        </v-card-text>
      </template>

      <v-card-text
        v-else
        class="image-picker-body image-picker-upload-body"
      >
        <div
          class="image-picker-dropzone"
          :class="{ dragging: isDragging, disabled: uploading }"
          @click="openFilePicker"
          @dragenter.prevent="isDragging = true"
          @dragover.prevent
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            class="image-picker-file-input"
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            @change="handleFileInput"
          >

          <v-icon size="38">mdi-cloud-upload-outline</v-icon>
          <strong>{{ selectedFile ? selectedFile.name : 'Choose an image' }}</strong>
          <span>
            Drop a file here or browse from your device. Images can be up to {{ maxFileSizeMb }} MB.
          </span>
        </div>

        <div
          v-if="selectedFile"
          class="image-picker-selected-file"
        >
          <v-icon size="18">mdi-file-image-outline</v-icon>
          <span>{{ selectedFile.name }}</span>
          <small>{{ prettyFileSize(selectedFile.size) }}</small>
        </div>

        <v-alert
          v-if="localUploadError || uploadError"
          type="error"
          variant="tonal"
          density="compact"
        >
          {{ localUploadError || uploadError }}
        </v-alert>

        <v-progress-linear
          v-if="uploading"
          indeterminate
          color="primary"
          rounded
        />
      </v-card-text>

      <v-card-actions class="image-picker-actions">
        <v-spacer />
        <v-btn
          variant="text"
          @click="closeModal"
        >
          Cancel
        </v-btn>
        <v-btn
          v-if="activeTab === 'upload'"
          color="primary"
          variant="flat"
          prepend-icon="mdi-cloud-upload-outline"
          :loading="uploading"
          :disabled="!selectedFile || Boolean(localUploadError)"
          @click="uploadSelectedFile"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>




<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  uploadError: {
    type: String,
    default: '',
  },
  selectedUrl: {
    type: String,
    default: '',
  },
  maxFileSizeMb: {
    type: Number,
    default: 10,
  },
  title: {
    type: String,
    default: 'Choose image',
  },
  subtitle: {
    type: String,
    default: 'Select an uploaded image for this field.',
  },
  emptyText: {
    type: String,
    default: 'No images found.',
  },
  deletable: {
    type: Boolean,
    default: false,
  },
  deleting: {
    type: [String, Number, null],
    default: null,
  },
  showStock: {
    type: Boolean,
    default: false,
  },
  stockImages: {
    type: Array,
    default: () => [],
  },
  stockLoading: {
    type: Boolean,
    default: false,
  },
  stockEmptyText: {
    type: String,
    default: 'No curated stock photos for this industry yet.',
  },
})

const emit = defineEmits([
  'update:modelValue',
  'select',
  'upload',
  'delete',
])

const search = ref('')
const activeTab = ref('library')
const fileInput = ref(null)
const selectedFile = ref(null)
const localUploadError = ref('')
const isDragging = ref(false)
const failedImageIds = ref(new Set())

// v-dialog expects a writable value. This bridges v-model to the parent.
const dialogOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

function closeModal() {
  dialogOpen.value = false
}

watch(
  () => props.modelValue,
  isOpen => {
    if (!isOpen) {
      resetUploadState()
      activeTab.value = 'library'
    } else {
      // Retry thumbnails each time the library opens in case an asset was
      // restored or its URL was refreshed since the previous attempt.
      failedImageIds.value = new Set()
    }
  }
)

function resetUploadState() {
  selectedFile.value = null
  localUploadError.value = ''
  isDragging.value = false
}

function imageUrl(image) {
  if (typeof image === 'string') {
    return image
  }

  return (
    image?.url ||
    image?.src ||
    image?.public_url ||
    image?.download_url ||
    ''
  )
}

function imageThumbnailUrl(image, url) {
  if (typeof image === 'string') {
    return url
  }

  return (
    image?.thumbnail_url ||
    image?.thumbnailUrl ||
    image?.thumbnail ||
    url
  )
}

function imageLabel(image, index) {
  if (typeof image === 'string') {
    return `Image ${index + 1}`
  }

  return (
    image?.caption ||
    image?.alt_text ||
    image?.name ||
    image?.filename ||
    `Image ${index + 1}`
  )
}

function handleThumbnailError(image, event) {
  const element = event.currentTarget

  // A stale derived thumbnail should not hide an otherwise valid original.
  // Mark the fallback on the element so a failing original cannot loop.
  if (
    image.thumbnailUrl !== image.url &&
    element?.dataset.fullImageFallback !== 'true'
  ) {
    element.dataset.fullImageFallback = 'true'
    element.src = image.url
    return
  }

  failedImageIds.value = new Set([...failedImageIds.value, image.id])
}

function normalizeImageList(list) {
  return list
    .map((image, index) => {
      const url = imageUrl(image)

      return {
        raw: image,
        id: image?.id || url || index,
        url,
        thumbnailUrl: imageThumbnailUrl(image, url),
        label: imageLabel(image, index),
      }
    })
    .filter(image => image.url)
}

const normalizedImages = computed(() => normalizeImageList(props.images))
const normalizedStockImages = computed(() => normalizeImageList(props.stockImages))

const currentTabImages = computed(() =>
  activeTab.value === 'stock' ? normalizedStockImages.value : visibleImages.value
)

const visibleImages = computed(() => {
  const query = search.value.trim().toLowerCase()

  if (!query) {
    return normalizedImages.value
  }

  return normalizedImages.value.filter(image =>
    `${image.label} ${image.url}`.toLowerCase().includes(query)
  )
})

function selectImage(image) {
  // Emit the original image data plus a normalized url for easy parent usage.
  const payload =
    typeof image.raw === 'string'
      ? { url: image.url }
      : {
          ...image.raw,
          url: image.url,
        }

  emit('select', payload)
  closeModal()
}

function openFilePicker() {
  if (props.uploading) return
  fileInput.value?.click()
}

function handleFileInput(event) {
  setSelectedFile(event.target.files?.[0])
  event.target.value = ''
}

function handleDrop(event) {
  isDragging.value = false

  if (props.uploading) return
  setSelectedFile(event.dataTransfer.files?.[0])
}

function setSelectedFile(file) {
  localUploadError.value = ''

  if (!file) {
    selectedFile.value = null
    return
  }

  if (!file.type.startsWith('image/')) {
    selectedFile.value = null
    localUploadError.value = 'Please choose an image file.'
    return
  }

  if (file.size > props.maxFileSizeMb * 1024 * 1024) {
    selectedFile.value = null
    localUploadError.value = `Images must be ${props.maxFileSizeMb} MB or smaller.`
    return
  }

  selectedFile.value = file
}

function uploadSelectedFile() {
  if (!selectedFile.value || props.uploading || localUploadError.value) return
  emit('upload', selectedFile.value)
}

function prettyFileSize(size) {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`
}
</script>

<style scoped>
.image-picker {
  overflow: hidden;
}

.image-picker-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.image-picker-header h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
}

.image-picker-header p {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.45;
}

.image-picker-search {
  padding: 16px 22px 0;
}

.image-picker-tabs {
  padding: 16px 22px 0;
}

.image-picker-body {
  min-height: 320px;
  padding: 18px 22px 22px;
}

.image-picker-state {
  min-height: 260px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.image-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 14px;
}

.image-picker-tile {
  position: relative;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  transition: border-color .15s, box-shadow .15s, transform .15s;
}

.image-picker-tile:hover,
.image-picker-tile.selected {
  border-color: #2f6fed;
  box-shadow: 0 8px 24px rgba(47, 111, 237, .14);
  transform: translateY(-1px);
}

.image-picker-tile-select {
  display: grid;
  gap: 8px;
  padding: 8px;
  width: 100%;
  background: none;
  border: none;
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
}

.image-picker-tile-select:disabled {
  cursor: not-allowed;
}

.image-picker-thumbnail {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 4px;
  background: var(--surface-alt);
}

.image-picker-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-picker-thumbnail-error {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 6px;
  color: var(--text-secondary);
  text-align: center;
}

.image-picker-thumbnail-error span {
  font-size: 11px;
  line-height: 1.25;
}

.image-picker-tile-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  line-height: 1.35;
}

.image-picker-tile-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: rgba(var(--v-theme-surface), 0.92);
  color: #c0392b;
  cursor: pointer;
  opacity: 0;
  transition: opacity .15s, background .15s;
}

.image-picker-tile:hover .image-picker-tile-delete,
.image-picker-tile.failed .image-picker-tile-delete {
  opacity: 1;
}

.image-picker-tile-delete:hover {
  background: #fef2f2;
}

.image-picker-tile-delete:disabled {
  color: var(--text-secondary);
  cursor: wait;
}

.image-picker-actions {
  padding: 10px 18px 16px;
  border-top: 1px solid var(--border-subtle);
}

.image-picker-upload-body {
  display: grid;
  align-content: start;
  gap: 14px;
}

.image-picker-dropzone {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  padding: 28px;
  border: 1px dashed var(--text-secondary);
  border-radius: 6px;
  background: var(--surface-alt);
  color: var(--text-secondary);
  text-align: center;
  cursor: pointer;
  transition: border-color .15s, background .15s, box-shadow .15s;
}

/* #eef4ff is a fixed light blue, not theme-reactive — pin the strong/span
   text inside to a fixed dark pair too, or they inherit
   var(--text-primary)/var(--text-secondary) and go near-white in dark mode. */
.image-picker-dropzone:hover,
.image-picker-dropzone.dragging {
  border-color: #2f6fed;
  background: #eef4ff;
  box-shadow: inset 0 0 0 1px rgba(47, 111, 237, .16);
}

.image-picker-dropzone:hover strong,
.image-picker-dropzone.dragging strong {
  color: #17231f;
}

.image-picker-dropzone:hover span,
.image-picker-dropzone.dragging span {
  color: #56635e;
}

.image-picker-dropzone.disabled {
  cursor: wait;
  opacity: .72;
}

.image-picker-dropzone strong {
  color: var(--text-primary);
  font-size: 15px;
  line-height: 1.3;
}

.image-picker-dropzone span {
  max-width: 360px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.45;
}

.image-picker-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.image-picker-selected-file {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 13px;
}

.image-picker-selected-file span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.image-picker-selected-file small {
  color: var(--text-secondary);
}
</style>
